import Styles from './FilesTable.module.scss'
import { PropertyValue } from 'common/PropertyValue/PropertyValue'
import { FileIcon } from 'screens/Finder/components/FileIcon/FileIcon'
import { useFilesTable } from './useFilesTable'
import { PersonPropertyValue, Property, PropertyType } from 'api/clients/PropertyClient'
import { HomeTable, HeaderType, RowType } from 'components/HomeTable/HomeTable'
import Person from 'common/PropertyValue/Types/Person'
import { useWorkspace } from 'hooks/useWorkspace'
import cx from 'classnames'
import { useRootStore } from 'stores/RootStore'
import { List } from 'screens/Finder/components/views/ListView/List'
import { LoadingRows } from './LoadingRows'
import { FilterType } from 'screens/RecentDocsContainer/Types'

interface Props {
  location?: string
  workspaceId: number
  folderKey?: string
  numItems?: number
  type?: FilterType
  skipProjects?: boolean
  fileTypes?: string
  numberOfColumns?: number
  spinnerCentered?: boolean
  skipCreatedByMe?: boolean
}

export const FilesTable = ({
  location = 'recents_home',
  workspaceId,
  folderKey,
  numItems,
  type,
  skipProjects = false,
  fileTypes,
  skipCreatedByMe = false,
}: Props) => {
  const {
    files,
    properties,
    sorting_property_id,
    sorting_direction,
    handleSort,
    fileLink,
    fileToAssignee,
    getAssignedProperty,
    getPersonPropertyValue,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isLast,
    removeFile,
  } = useFilesTable({ location, workspaceId, folderKey, numItems, type, skipProjects, fileTypes })
  const filesStore = useRootStore().files
  const folder = filesStore.get(folderKey || '')
  const workspace = useWorkspace(workspaceId)
  const filteredProperties = (properties || [])?.filter((p) => (skipCreatedByMe ? p.name !== 'Created by me' : true))

  if (!files.length && !isLoading) return <LoadingRows loadingCellWidth={180} isInitialLoad />

  const isActiveColumn = (property_id: number) => sorting_property_id === (property_id || null)

  const headers: HeaderType[] = (filteredProperties || [])?.map((property: Property) => ({
    id: property.id,
    onClick: () => handleSort(property),
    name: property.name,
    isActive: isActiveColumn(property.id),
  }))

  const rows: RowType[] = files.map((file: any, index: number) => ({
    file,
    url: file.nodeType === 'sectionHeader' ? '' : fileLink(file),
    type: file.nodeType,
    title: file.title,
    icon: (
      <div
        className={cx(Styles.iconContainer, {
          [Styles.docIconContainer]: file.type === 'article' && !file.external_link && !file.channel_emoji,
        })}
      >
        <FileIcon file={file} size="lg" color={'var(--dark80)'} />
      </div>
    ),
    columns: filteredProperties?.map((property: Property, idx: number) => ({
      id: property.id,
      value:
        property.type === PropertyType.Person ? (
          <Person
            userClassName={Styles.propertyValueCell}
            propertyValue={(getPersonPropertyValue(file, property) as PersonPropertyValue) || ''}
          />
        ) : (
          <PropertyValue
            inline
            assignee={fileToAssignee(file)}
            property={property}
            selectedPropertyValues={getAssignedProperty(file, property)?.values || []}
            onSelect={() => {}}
            isEditing={false}
            setIsEditing={() => {}}
            className={Styles.propertyValueCell}
            valueClassName={Styles.propertyValueCell}
          />
        ),
      lastCell: idx + 1 === filteredProperties.length,
      firstCell: idx === 0,
    })),
    isLast: isLast(index),
  }))

  if (['shared_with_me', 'recents_home'].includes(location)) {
    return (
      <HomeTable
        rows={rows}
        headers={headers}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        isLoading={isLoading}
        sortDirection={sorting_direction}
        enableAddNew={!!folder?.permissions?.can_edit && !!workspace && !workspace.guest}
        onDelete={removeFile}
      />
    )
  }

  return <List />
}
